import type { CSSProperties } from 'react';

import type { TFunction } from 'i18next';

import {
  getFrameworkLibraryOptionMap,
  resolveOpsTagSegmentLabel,
} from '@/pages/data-center/artifact-center/components/OpsPublishModal/constants';
import { formatPlazaArtifactDisplayName } from '@/pages/data-center/artifact-center/shared/utils/plazaDisplayName';

import {
  type FilterChipTone,
  HOT_TAG_TONE,
  VENDOR_TONE,
  resolveOpsTagTone,
  resolveSkillTypeTone,
} from '../model-repo/constants/filterTone';
import type { PublishedArtifactItem, PublishedArtifactRawData, PublishedArtifactType } from '../service';
import { publicPlazaPaths } from './publicPlazaPaths';

/** 卡片 icon 背景色板（模型 / 技能 / 应用共用） */
export const ARTIFACT_MARK_COLORS = ['#2455f5', '#7c4dff', '#0f9f78', '#e46c2d', '#cf3f79', '#1684c7'] as const;

/** 来源类型品牌名（与数据中心 OPTIONS_* 一致，品牌词不走 i18n） */
const SOURCE_TYPE_BRAND_LABELS: Record<string, string> = {
  modelscope: 'ModelScope',
  huggingface: 'Hugging Face',
};

/** 卡片标签（含与筛选栏对齐的模块色调） */
export interface PublishedArtifactTagItem {
  key: string;
  label: string;
  /** 原始标签 value，用于与筛选栏复用语义图标。 */
  value: string;
  /** 与左侧筛选芯片 data-tone 一致；缺省走卡片默认紫 */
  tone?: FilterChipTone;
  /** 推理框架图标（门户卡片展示） */
  icon?: string;
}

/** 解析公开制品 rawData JSON；解析失败时返回空对象 */
export function parsePublishedArtifactRawData(rawData?: string): PublishedArtifactRawData {
  if (!rawData) return {};
  try {
    const parsed = JSON.parse(rawData);
    return parsed && typeof parsed === 'object' ? (parsed as PublishedArtifactRawData) : {};
  } catch (error) {
    console.error('parse published artifact rawData failed', error);
    return {};
  }
}

/** 取厂商展示缩写（用于卡片头像） */
export function getVendorMark(vendor?: string, name?: string) {
  const source = (vendor || name || '').trim();
  if (!source) return '--';
  // 中文取首字，英文取前两字母
  if (/[\u4e00-\u9fff]/.test(source[0])) {
    return source.slice(0, 1);
  }
  return source.slice(0, 2).toUpperCase();
}

/** 根据稳定种子从固定色板取色，避免列表重渲染时颜色跳动 */
export function getArtifactMarkColor(seed?: string) {
  const palette = ARTIFACT_MARK_COLORS;
  if (!seed) return palette[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

/** 实色底 + 白字：模型 / 应用头像徽标 */
export function getArtifactMarkBadgeStyle(seed?: string): CSSProperties {
  const color = getArtifactMarkColor(seed);
  return {
    color: '#fff',
    background: color,
  };
}

export function getArtifactVendor(raw: PublishedArtifactRawData) {
  return raw.vendor || '';
}

/** 租户空间：优先列表行字段，其次 rawData.workspace */
export function getArtifactWorkspace(item: PublishedArtifactItem, raw: PublishedArtifactRawData) {
  const fromItem = typeof item.workspace === 'string' ? item.workspace.trim() : '';
  if (fromItem) return fromItem;
  const fromRaw = typeof raw.workspace === 'string' ? raw.workspace.trim() : '';
  return fromRaw;
}

/** 标签列表（过滤空值） */
export function getArtifactTags(raw: PublishedArtifactRawData) {
  return Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : [];
}

/** 固定标签 value 列表：优先顶层 opsTags，其次 rawData.opsTag / opsTags */
export function getArtifactOpsTags(item: PublishedArtifactItem, raw: PublishedArtifactRawData): string[] {
  const fromItem = Array.isArray(item.opsTags) ? item.opsTags : [];
  const fromRaw = Array.isArray(raw.opsTag) ? raw.opsTag : Array.isArray(raw.opsTags) ? raw.opsTags : [];
  const source = fromItem.length ? fromItem : fromRaw;
  return source.filter((tag): tag is string => typeof tag === 'string' && !!tag);
}

/** 推理框架标签：优先顶层 inferenceFrameworkTags，其次 rawData.inferenceFrameworkTags */
export function getArtifactInferenceFrameworkTags(item: PublishedArtifactItem, raw: PublishedArtifactRawData): string[] {
  const fromItem = Array.isArray(item.inferenceFrameworkTags) ? item.inferenceFrameworkTags : [];
  const fromRaw = Array.isArray(raw.inferenceFrameworkTags) ? raw.inferenceFrameworkTags : [];
  const source = fromItem.length ? fromItem : fromRaw;
  return source.filter((tag): tag is string => typeof tag === 'string' && !!tag);
}

/** 热门标签：优先顶层 tags，其次 rawData.tag */
export function getArtifactHotTags(item: PublishedArtifactItem, raw: PublishedArtifactRawData): string[] {
  const fromItem = Array.isArray(item.tags) ? item.tags.filter(Boolean) : [];
  if (fromItem.length) return fromItem;
  return getArtifactTags(raw);
}

/** 固定标签展示文案：路径取叶子 label */
function formatOpsTagLeafLabel(opsTagValue: string): string {
  const leaf = opsTagValue.includes('/') ? opsTagValue.split('/').pop()! : opsTagValue;
  return resolveOpsTagSegmentLabel(leaf);
}

/** 构建卡片标签：模型/技能优先固定标签（按模块上色），再补热门标签（统一 rose） */
export function buildPublishedArtifactTagItems(
  item: PublishedArtifactItem,
  raw: PublishedArtifactRawData,
  artifactType: PublishedArtifactType,
): PublishedArtifactTagItem[] {
  const hotTags = getArtifactHotTags(item, raw);

  // 应用广场：卡片标签均为热门标签，统一 HOT_TAG_TONE
  if (artifactType === 'ai_application') {
    return hotTags.map((tag) => ({ key: `hot:${tag}`, label: tag, value: tag, tone: HOT_TAG_TONE }));
  }

  const opsTags = getArtifactOpsTags(item, raw);
  const items: PublishedArtifactTagItem[] = [];

  if (artifactType === 'skill') {
    for (const value of opsTags) {
      items.push({
        key: `ops:${value}`,
        label: formatOpsTagLeafLabel(value),
        value,
        tone: resolveSkillTypeTone(value),
      });
    }
  } else {
    const modelType = item.modelType || raw.modelType;
    for (const value of opsTags) {
      items.push({
        key: `ops:${value}`,
        label: formatOpsTagLeafLabel(value),
        value,
        tone: resolveOpsTagTone(value, modelType),
      });
    }

    const frameworkOptionMap = getFrameworkLibraryOptionMap();
    for (const value of getArtifactInferenceFrameworkTags(item, raw)) {
      const option = frameworkOptionMap.get(value);
      items.push({
        key: `framework:${value}`,
        label: option?.label ?? value,
        value,
        tone: VENDOR_TONE,
        icon: option?.icon,
      });
    }
  }

  // 热门标签统一 rose；与固定标签文案重复时跳过
  for (const tag of hotTags) {
    if (items.some((entry) => entry.label === tag)) continue;
    items.push({ key: `hot:${tag}`, label: tag, value: tag, tone: HOT_TAG_TONE });
  }

  return items;
}

/** 优先展示 git / http / docker 体积字段 */
export function getArtifactSourceSize(raw: PublishedArtifactRawData) {
  return raw.gitSourceSize || raw.httpSourceSize || raw.dockerSourceSize || '';
}

/** 应用包格式（docker / binary 等） */
export function getArtifactPackageFormat(raw: PublishedArtifactRawData) {
  return raw.packageFormat || '';
}

/**
 * 来源类型 code：模型读 options.modelRepo.modelRepoType，技能读 options.skill.skillSourceType。
 */
export function getArtifactSourceTypeCode(raw: PublishedArtifactRawData, artifactType?: PublishedArtifactType) {
  if (artifactType === 'skill') {
    return raw.options?.skill?.skillSourceType || '';
  }
  if (artifactType === 'model_repo') {
    return raw.options?.modelRepo?.modelRepoType || '';
  }
  return raw.options?.skill?.skillSourceType || raw.options?.modelRepo?.modelRepoType || '';
}

/** 来源类型展示文案：对齐数据中心 renderModelType / renderSkillType */
export function formatArtifactSourceTypeLabel(code: string | undefined, t: TFunction) {
  if (!code) return '';
  if (code === 'stackmodel') return t('data-center:git.upload');
  return SOURCE_TYPE_BRAND_LABELS[code] || code;
}

/**
 * 来源名：模型 options.modelRepo.modelName，技能 options.skill.skillName，缺省回退 gitSourceUrl。
 */
export function getArtifactSourceName(raw: PublishedArtifactRawData, artifactType?: PublishedArtifactType) {
  if (artifactType === 'skill') {
    return raw.options?.skill?.skillName || raw.gitSourceUrl || '';
  }
  if (artifactType === 'model_repo') {
    return raw.options?.modelRepo?.modelName || raw.gitSourceUrl || '';
  }
  return raw.options?.skill?.skillName || raw.options?.modelRepo?.modelName || raw.gitSourceUrl || '';
}

/** 卡片元信息项：仅展示 value，hover 时 tooltip 展示 label */
export interface ArtifactMetaItem {
  value: string;
  tooltip: string;
}

/** 门户公开制品卡片视图模型（首页与广场页共用） */
export interface PublishedArtifactCardView {
  key: string;
  name: string;
  /** 卡片标题：workspace/name，无 workspace 时回退 name */
  displayName: string;
  description: string;
  vendor: string;
  mark: string;
  markStyle: CSSProperties;
  /** @deprecated 兼容旧用法；优先使用 tagItems */
  tags: string[];
  /** 带模块色调的标签（模型广场固定标签对齐左侧筛选色） */
  tagItems: PublishedArtifactTagItem[];
  item: PublishedArtifactItem;
  /** 卡片右上角来源 / 制品文件类型 */
  topRightMeta: ArtifactMetaItem | null;
  /** 卡片底部左侧元信息（源模型/源skill 或 大小） */
  bottomLeftMeta: ArtifactMetaItem | null;
  /** 卡片底部右侧元信息（大小 / 厂商 / 版本） */
  bottomRightMeta: ArtifactMetaItem | null;
}

/** 应用广场版本详情路径：父应用取 rawData.abstractId，版本取 artifactId / rawData.id */
export function getPublishedAiApplicationDetailPath(item: PublishedArtifactItem) {
  const raw = parsePublishedArtifactRawData(item.rawData);
  const artifactId = typeof raw.abstractId === 'string' && raw.abstractId ? raw.abstractId : '';
  const versionId = (typeof raw.id === 'string' && raw.id ? raw.id : '') || item.artifactId || '';
  return publicPlazaPaths.appCenterDetail(String(item.datacenter || ''), artifactId, versionId);
}

/** 从公开制品项构建卡片视图（含类型差异化的元信息） */
export function buildPublishedArtifactCardView(
  item: PublishedArtifactItem,
  artifactType: PublishedArtifactType,
  t: TFunction,
): PublishedArtifactCardView {
  const raw = parsePublishedArtifactRawData(item.rawData);
  const vendor = getArtifactVendor(raw) || item.vendor || '';
  const workspace = getArtifactWorkspace(item, raw);
  const displayName = formatPlazaArtifactDisplayName({ workspace, name: item.name });
  const size = getArtifactSourceSize(raw) || '--';
  const tagItems = buildPublishedArtifactTagItems(item, raw, artifactType);
  const tags = tagItems.map((tag) => tag.label);
  const sourceType = formatArtifactSourceTypeLabel(getArtifactSourceTypeCode(raw, artifactType), t) || '--';
  const sourceName = getArtifactSourceName(raw, artifactType) || '--';
  const packageFormat = getArtifactPackageFormat(raw) || '--';

  /** 右上角：模型/技能展示来源类型，应用展示制品文件类型 */
  const topRightMeta: ArtifactMetaItem | null =
    artifactType === 'ai_application'
      ? { value: packageFormat, tooltip: t('data-center:artifact.package.format') }
      : { value: sourceType, tooltip: t('common:source') };

  /** 底部左侧：源名称（模型/技能）或大小（应用） */
  const bottomLeftMeta: ArtifactMetaItem =
    artifactType === 'ai_application'
      ? { value: size, tooltip: t('data-center:size') }
      : {
          value: sourceName,
          tooltip: artifactType === 'skill' ? t('data-center:source.skill') : t('data-center:source.model'),
        };

  const vendorDisplay = vendor || item.datacenter || '--';

  /** 底部右侧：大小（模型）/ 厂商（技能）/ 版本（应用） */
  const bottomRightMeta: ArtifactMetaItem =
    artifactType === 'ai_application'
      ? { value: item.version || '--', tooltip: t('common:version') }
      : artifactType === 'skill'
        ? { value: vendorDisplay, tooltip: t('common:manufacturer') }
        : { value: size, tooltip: t('data-center:size') };

  return {
    key: `${item.datacenter}-${item.artifactId}-${item.version}`,
    name: item.name,
    displayName,
    description: item.description || '',
    vendor: vendorDisplay,
    mark: getVendorMark(vendor, item.name),
    markStyle: getArtifactMarkBadgeStyle(`${item.datacenter}-${item.artifactId}-${item.version}`),
    tags,
    tagItems,
    item,
    topRightMeta,
    bottomLeftMeta,
    bottomRightMeta,
  };
}
